import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import {
  Animated,
  Dimensions,
  Pressable,
  ScrollView,
  Text,
  View,
  Platform,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import { setTeamMemberCount } from "../../store/slices/ticketingPortalSlice";
import { useToast } from "../../components/LhToastContainer";
import { formatUserName } from "../../utils/emailTouserName";
import { getLocalTimeZoneAbbr } from "../../utils/dateFormatter";
import useTicketingAccess from "./hooks/useTicketingAccess";
import LHLoader from "../../components/LHLoader";
import useSustainTeamMembers from "./hooks/useSustainTeamMembers";
import { useTheme } from "../../theme";
import {
  updateSustainTeamMember,
  updateSustainTeamSchedule,
  SustainTeamScheduleEntry,
  UpdateScheduleBody,
  fetchAllMembersOffDutyStatus,
  activateAllMembersOffDuty,
  deactivateAllMembersOffDuty,
  SustainAllMembersOffDutyResponse,
} from "../../store/thunks/ticketingPortalThunk";
import { createApiClient } from "../../api/apiClient";
import { s } from "./SustainTeamManagementScreen.styles";
import { fetchAvailableTicketingTeams } from "../../store/thunks/ticketingPortalThunk";

import {
  TeamMember,
  CalendarDay,
  ActiveView,
  SortDirection,
  EditFormState,
  Props,
  toDateKey,
  applyScheduleToMembers,
  buildScheduleJson,
  isValidTimeValue,
  getCstParts,
  buildCalendarDays,
  RosterFilters,
  EMPTY_ROSTER_FILTERS,
  FilterOption,
  TEAM_COLORS,
} from "./components/SustainTeamTypes";
import TeamTable          from "./components/TeamTable";
import CalendarTable      from "./components/CalendarTable";
import CalendarViewSkeleton from "./components/CalendarViewSkeleton";
import EditMemberPanel    from "./components/EditMemberPanel";
import MobileTeamView     from "./components/MobileTeamView";
import AllOffDutyModal, { AllOffDutyRange } from "./components/AllOffDutyModal";

// ─── Main screen ──────────────────────────────────────────────────────────────
const SustainTeamManagementScreen: React.FC<Props> = ({ onNavigate }) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const { isSustainTeamMember, accessLoading } = useTicketingAccess();

  // Route guard – redirect non-sustain users back to the portal landing
  useEffect(() => {
    if (!accessLoading && !isSustainTeamMember) {
      onNavigate("Ticketing Portal");
    }
  }, [accessLoading, isSustainTeamMember, onNavigate]);

  const toast = useToast();
  const dispatch = useDispatch<AppDispatch>();
  const authUser = useSelector((state: RootState) => state.auth.user);
  const userGpid  = useSelector((state: RootState) => (state.appData as any)?.userInfo?.GPID ?? "");
  const currentUserEmail = useMemo(
    () => (authUser?.email || authUser?.username || "").toLowerCase(),
    [authUser],
  );
  const [search, setSearch] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);

  // Suppress native browser focus ring on the search input (web only)
  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") return;
    const styleId = "sustain-search-no-ring";
    if (!document.getElementById(styleId)) {
      const el = document.createElement("style");
      el.id = styleId;
      el.textContent =
        'input[data-nofocusring="1"]:focus,' +
        'input[data-nofocusring="1"]:focus-visible{outline:none!important;box-shadow:none!important;}';
      document.head.appendChild(el);
    }
  }, []);
  const [activeView, setActiveView] = useState<ActiveView>("team");
  const [memberSortDirection, setMemberSortDirection] = useState<SortDirection>("asc");
  const [teamData, setTeamData] = useState<TeamMember[]>([]);
  const [teamLoadError, setTeamLoadError] = useState(false);
  const [rosterFilters, setRosterFilters] = useState<RosterFilters>(EMPTY_ROSTER_FILTERS);

  // ─── Permission: is the logged-in user a Lead? ───────────────────────────────
  const isCurrentUserLead = useMemo(
    () => teamData.some((m) => m.email.toLowerCase() === currentUserEmail && m.role === "lead"),
    [teamData, currentUserEmail],
  );
  // Leads can edit any member; Members can only edit themselves.
  const canEditMember = useCallback(
    (member: TeamMember) =>
      isCurrentUserLead || member.email.toLowerCase() === currentUserEmail,
    [isCurrentUserLead, currentUserEmail],
  );

  // ─── Manager field: distinct managers for the roster filter + Leads for the Edit panel ──
  const managerOptions = useMemo<FilterOption[]>(() => {
    const seen = new Map<string, string>();
    teamData.forEach((m) => {
      if (m.managerEmail && !seen.has(m.managerEmail.toLowerCase())) {
        seen.set(m.managerEmail.toLowerCase(), m.managerEmail);
      }
    });
    return Array.from(seen.values())
      .sort((a, b) => a.localeCompare(b))
      .map((email) => ({ value: email, label: formatUserName(email) }));
  }, [teamData]);

  const leadOptions = useMemo(
    () => teamData.filter((m) => m.role === "lead").map((m) => ({ email: m.email, name: m.name, teamId: m.teamId ?? null })),
    [teamData],
  );

  // ─── Team filter options: TKT_Teams list, used for the roster "Team" column + filter ───
  const availableTicketingTeams = useSelector(
    (state: RootState) => state.ticketingPortal.availableTicketingTeams
  );
  const ticketingTeams = useSelector(
    (state: RootState) => state.ticketingPortal.ticketingTeams
  );
  const teamOptions = useMemo<FilterOption[]>(
    () => availableTicketingTeams.map((team, index) => ({
      value: String(team.id),
      label: team.name,
      color: TEAM_COLORS[index % TEAM_COLORS.length],
    })),
    [availableTicketingTeams],
  );
  const allTeamOptions = useMemo<FilterOption[]>(
    () => ticketingTeams.map((team, index) => ({
      value: String(team.id),
      label: team.name,
      color: TEAM_COLORS[index % TEAM_COLORS.length],
    })),
    [ticketingTeams],
  );
  useEffect(() => {
    dispatch(fetchAvailableTicketingTeams());
  }, [dispatch]);

  // ─── API: fetch team members (debounced search) ─────────────────────────────
  const { members: apiMembers, loading: teamLoading, error: apiError, refetch: refetchTeamMembers } = useSustainTeamMembers(search, rosterFilters);

  // Re-fetch team members every time the user switches back to the Team tab
  const isInitialViewMount = useRef(true);
  useEffect(() => {
    if (isInitialViewMount.current) {
      isInitialViewMount.current = false;
      return;
    }
    if (activeView === "team") {
      refetchTeamMembers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);

  const rawScheduleRef = useRef<SustainTeamScheduleEntry[]>([]);

  // Sync API data → local state
  useEffect(() => {
    const base = apiMembers as TeamMember[];
    const merged =
      rawScheduleRef.current.length > 0
        ? applyScheduleToMembers(base, rawScheduleRef.current)
        : base;
    setTeamData(merged);
    setTeamLoadError(!!apiError);
  }, [apiMembers, apiError]);

  // ─── API: fetch schedule when Calendar View is activated ─────────────────────
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const scheduleLoadedRef = useRef(false);

  // ─── Calendar view filters ────────────────────────────────────────────────────
  const [calendarTeamFilters, setCalendarTeamFilters] = useState<string[]>([]);
  const [calendarManagerFilters, setCalendarManagerFilters] = useState<string[]>([]);
  const [calendarMemberCount, setCalendarMemberCount] = useState(0);

  /** Build query string for GET /schedule with optional team and manager filters. */
  const buildScheduleQuery = useCallback((teamFilters: string[], managerFilters: string[]): string => {
    const params = new URLSearchParams();
    teamFilters.forEach((id) => params.append("team", id));
    managerFilters.forEach((email) => params.append("manager", email));
    return params.toString();
  }, []);

  const refreshScheduleData = useCallback(async (teamFilters?: string[], managerFilters?: string[]) => {
    const apiClient = createApiClient();
    const qs = buildScheduleQuery(teamFilters ?? calendarTeamFilters, managerFilters ?? calendarManagerFilters);
    const url = `/ticketing/sustain-team/schedule${qs ? `?${qs}` : ""}`;
    const resp = await apiClient.get<{
      message: string; totalCount: number;
      data: SustainTeamScheduleEntry[]; success: boolean;
    }>(url);
    const freshEntries = resp.data?.data ?? [];
    rawScheduleRef.current = freshEntries;
    setRawScheduleData(freshEntries);
    setCalendarMemberCount(resp.data?.totalCount ?? freshEntries.length);
    setTeamData((prev) => applyScheduleToMembers(prev, freshEntries));
    scheduleLoadedRef.current = true;
  }, [buildScheduleQuery, calendarTeamFilters, calendarManagerFilters]);

  useEffect(() => {
    if (activeView !== "calendar") return;
    let cancelled = false;
    const loadSchedule = async () => {
      setScheduleLoading(true);
      setScheduleError(null);
      try {
        const api = createApiClient();
        const qs = buildScheduleQuery(calendarTeamFilters, calendarManagerFilters);
        const url = `/ticketing/sustain-team/schedule${qs ? `?${qs}` : ""}`;
        const response = await api.get<{
          message: string; totalCount: number;
          data: SustainTeamScheduleEntry[]; success: boolean;
        }>(url);
        if (cancelled) return;
        const entries: SustainTeamScheduleEntry[] = response.data?.data ?? [];
        rawScheduleRef.current = entries;
        scheduleLoadedRef.current = true;
        setRawScheduleData(entries);
        setCalendarMemberCount(response.data?.totalCount ?? entries.length);
        setTeamData((prev) => applyScheduleToMembers(prev, entries));
        // Capture manager options from the unfiltered response so the dropdown
        // always shows the full list regardless of which filters are currently active.
        if (calendarTeamFilters.length === 0 && calendarManagerFilters.length === 0) {
          const seen = new Map<string, string>();
          entries.forEach((e) => {
            const em = e.managerEmail ?? null;
            if (em && !seen.has(em.toLowerCase())) seen.set(em.toLowerCase(), em);
          });
          setAllCalendarManagerOptions(
            Array.from(seen.values())
              .sort((a, b) => a.localeCompare(b))
              .map((em) => ({ value: em, label: formatUserName(em) })),
          );
        }
      } catch (err: any) {
        if (!cancelled) setScheduleError(err?.message ?? "Failed to load schedule");
      } finally {
        if (!cancelled) setScheduleLoading(false);
      }
    };
    loadSchedule();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, calendarTeamFilters, calendarManagerFilters]);

  // ─── Calendar schedule map: email → dateKey → is_working ─────────────────────
  const [rawScheduleData, setRawScheduleData] = useState<SustainTeamScheduleEntry[]>([]);

  /**
   * Stable manager options for the calendar filter bar.
   * Captured from the **unfiltered** schedule response (no team/manager params active)
   * so the dropdown always shows the full manager list regardless of active filters.
   * Falls back to the roster managerOptions when no schedule data is loaded yet.
   */
  const [allCalendarManagerOptions, setAllCalendarManagerOptions] = useState<FilterOption[]>([]);

  const calendarManagerOptions = allCalendarManagerOptions.length > 0
    ? allCalendarManagerOptions
    : managerOptions;

  /**
   * Members visible in the Calendar View — restricted to the emails returned by the
   * current GET /schedule call (which already applies the team/manager filters).
   * Without this, CalendarTable would always show ALL members from `teamData`
   * (populated by the Members API), ignoring schedule-level server-side filtering.
   */
  const calendarTeamData = useMemo(() => {
    if (rawScheduleData.length === 0) return teamData;
    const emailsInSchedule = new Set(
      rawScheduleData.map((e) => (e.memberEmail ?? "").toLowerCase()),
    );
    return teamData.filter((m) => emailsInSchedule.has(m.email.toLowerCase()));
  }, [teamData, rawScheduleData]);

  const calendarScheduleMap = useMemo<Record<string, Record<string, boolean>>>(() => {
    const map: Record<string, Record<string, boolean>> = {};
    rawScheduleData.forEach((entry) => {
      const key = (entry.memberEmail ?? "").toLowerCase();
      map[key] = {};
      if (entry.schedule) {
        const DAY_OFFSET: Record<string, number> = {
          Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6,
        };
        Object.entries(entry.schedule).forEach(([weekStart, weekDays]) => {
          const [wy, wm, wd] = weekStart.split("-").map(Number);
          Object.entries(weekDays).forEach(([dayName, { is_working }]) => {
            const offset = DAY_OFFSET[dayName];
            if (offset === undefined) return;
            const d = new Date(wy, wm - 1, wd + offset);
            map[key][toDateKey(d)] = is_working;
          });
        });
      }
    });
    return map;
  }, [rawScheduleData]);

  const [calendarToggles, setCalendarToggles] =
    useState<Record<string, Record<string, boolean>>>({});

  const [editMemberId, setEditMemberId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditFormState | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [teamFieldError, setTeamFieldError] = useState<string | null>(null);
  const [initialForm, setInitialForm] = useState<string>("");
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [panelVisible, setPanelVisible] = useState(false);
  const cstNow = getCstParts();
  const _localNowDate = new Date();
  const localTimeZoneAbbr = getLocalTimeZoneAbbr(_localNowDate);
  const localNowTime = (() => {
    const h = _localNowDate.getHours();
    const m = _localNowDate.getMinutes();
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ampm} ${localTimeZoneAbbr}`;
  })();
  const [calendarDays] = useState<CalendarDay[]>(() =>
    buildCalendarDays(new Date(cstNow.year, cstNow.month - 1, cstNow.day))
  );

  const PANEL_WIDTH = 440;
  const slideAnim = useRef(new Animated.Value(PANEL_WIDTH)).current;

  // Keep Redux in sync so the landing page tile shows the live member count
  useEffect(() => {
    dispatch(setTeamMemberCount(teamData.length));
  }, [dispatch, teamData.length]);

  useEffect(() => {
    if (editMemberId) {
      setPanelVisible(true);
      slideAnim.setValue(PANEL_WIDTH);
      Animated.timing(slideAnim, { toValue: 0, duration: 280, useNativeDriver: true }).start();
    } else {
      Animated.timing(slideAnim, { toValue: PANEL_WIDTH, duration: 220, useNativeDriver: true }).start(() => setPanelVisible(false));
    }
  }, [editMemberId]);

  const openEdit = (member: TeamMember) => {
    if (!canEditMember(member)) {
      toast.show("Access denied. You can only edit your own settings.", "error");
      return;
    }
    // Determine current duty status from member's working status
    const currentlyOnDuty = member.workingStatus === "On Duty";
    
    const form: EditFormState = {
      role: member.role,
      // Initialize onDuty based on member's actual working status (not hardcoded to true)
      onDuty: currentlyOnDuty,
      managerEmail: member.managerEmail ?? "",
      teamId: member.teamId ?? null,
      // Populate from API response; fall back to sensible defaults for brand-new members.
      channelsOnDuty:  member.channelsOnDuty  ?? { nav: true,  email: true,  teams: true  },
      channelsOffDuty: member.channelsOffDuty ?? { nav: false, email: true,  teams: false },
      triggers: member.triggers ?? { newTicket: true, assigned: true, unassigned: true, comment: true, agingTicket: true },
      // Use user's actual preference from API; only apply defaults for brand new members
      // Respect user's independent choice: if they disabled aging ticket on duty, keep it disabled
      triggersOnDuty: {
        newTicket:  member.triggersOnDuty?.newTicket ?? true,
        assigned:   member.triggersOnDuty?.assigned ?? true,
        unassigned: member.triggersOnDuty?.unassigned ?? true,
        comment:    member.triggersOnDuty?.comment ?? true,
        // Use actual stored value; if undefined (new member), default to true
        agingTicket: member.triggersOnDuty?.agingTicket !== undefined ? member.triggersOnDuty.agingTicket : true,
      },
      triggersOffDuty: {
        newTicket:  member.triggersOffDuty?.newTicket ?? false,
        assigned:   member.triggersOffDuty?.assigned ?? true,
        unassigned: member.triggersOffDuty?.unassigned ?? false,
        comment:    member.triggersOffDuty?.comment ?? false,
        // Use actual stored value; if undefined (new member), default to false
        agingTicket: member.triggersOffDuty?.agingTicket !== undefined ? member.triggersOffDuty.agingTicket : false,
      },
      scope: member.scope,
      customUrgencies: member.customUrgencies ?? [],
    };
    setEditMemberId(member.id);
    setEditForm(form);
    setInitialForm(JSON.stringify(form));
    setSaveError(null);
    setTeamFieldError(null);
  };

  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [showLeadWarning, setShowLeadWarning] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // ─── Calendar: per-row saving state ──────────────────────────────────────────
  const [savingMemberIds, setSavingMemberIds] = useState<Set<string>>(new Set<string>());
  const savingAnimValue = useRef(new Animated.Value(0.45)).current;
  const savingLoopRef   = useRef<Animated.CompositeAnimation | null>(null);
  useEffect(() => {
    if (savingMemberIds.size > 0) {
      if (!savingLoopRef.current) {
        savingLoopRef.current = Animated.loop(
          Animated.sequence([
            Animated.timing(savingAnimValue, { toValue: 1,    duration: 700, useNativeDriver: true }),
            Animated.timing(savingAnimValue, { toValue: 0.45, duration: 700, useNativeDriver: true }),
          ]),
        );
        savingLoopRef.current.start();
      }
    } else {
      savingLoopRef.current?.stop();
      savingLoopRef.current = null;
      savingAnimValue.setValue(0.45);
    }
  }, [savingMemberIds.size, savingAnimValue]);
  const markRowSaving   = (id: string) => setSavingMemberIds(prev => new Set([...prev, id]));
  const unmarkRowSaving = (id: string) => setSavingMemberIds(prev => { const n = new Set(prev); n.delete(id); return n; });

  // ─── Calendar: batch save (fallback) ─────────────────────────────────────────
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);
  const [saveScheduleError, setSaveScheduleError] = useState<string | null>(null);

  const hasCalendarChanges = Object.values(calendarToggles).some(
    (toggles) => Object.keys(toggles).length > 0,
  );

  const saveSchedule = async () => {
    setIsSavingSchedule(true);
    setSaveScheduleError(null);
    try {
      const performerEmail = authUser?.email || authUser?.username || "";
      const changedEmailKeys = Object.keys(calendarToggles).filter(
        (k) => Object.keys(calendarToggles[k]).length > 0,
      );
      const membersToSave = rawScheduleData.filter(
        (e) => changedEmailKeys.includes((e.memberEmail ?? "").toLowerCase()),
      );
      await Promise.all(
        membersToSave.map((entry) => {
          const emailKey     = (entry.memberEmail ?? "").toLowerCase();
          const scheduleJson = buildScheduleJson(emailKey, calendarDays, calendarScheduleMap, calendarToggles);
          const member = teamData.find((m) => m.email.toLowerCase() === emailKey);
          const { shiftStartCst, shiftEndCst } = resolveScheduleShiftTimes(entry, member);
          const body: UpdateScheduleBody = {
            performerEmail,
            performerGpid: userGpid,
            shiftStartCst,
            shiftEndCst,
            scheduleJson,
          };
          return dispatch(updateSustainTeamSchedule(entry.memberId, body) as any);
        }),
      );
      setCalendarToggles({});
      await refreshScheduleData();
      toast.show("Schedule saved successfully.", "success");
      const othersUpdated = membersToSave
        .filter((e) => (e.memberEmail ?? "").toLowerCase() !== currentUserEmail)
        .map((e) => formatUserName(e.memberEmail ?? ""));
      if (othersUpdated.length > 0) {
        toast.show(
          `${othersUpdated.join(", ")} ${othersUpdated.length === 1 ? "has" : "have"} been notified of their schedule update.`,
          "info",
        );
      }
    } catch (err: any) {
      const msg = err?.message ?? "Failed to save schedule";
      setSaveScheduleError(msg);
      toast.show(msg, "error");
    } finally {
      setIsSavingSchedule(false);
    }
  };

  const closeEdit = () => {
    const hasChanges = editForm && JSON.stringify(editForm) !== initialForm;
    if (hasChanges) {
      setShowUnsavedDialog(true);
    } else {
      setEditMemberId(null);
      setEditForm(null);
    }
  };

  const discardAndClose = () => {
    setShowUnsavedDialog(false);
    setEditMemberId(null);
    setEditForm(null);
  };

  const mapScope = (scope: "all" | "assigned" | "custom"): string => {
    if (scope === "all")      return "ALL_TICKETS";
    if (scope === "assigned") return "ASSIGNED_ONLY";
    return "CUSTOM";
  };

  const withSeconds = (value: string): string => {
    if (/^\d{2}:\d{2}:\d{2}$/.test(value)) return value;
    if (/^\d{2}:\d{2}$/.test(value)) return `${value}:00`;
    return value;
  };

  const resolveScheduleShiftTimes = (
    scheduleEntry?: SustainTeamScheduleEntry,
    member?: TeamMember,
  ): { shiftStartCst: string; shiftEndCst: string } => {
    const shiftStartCst = scheduleEntry?.shiftStartCst || (member?.startHour ? withSeconds(member.startHour) : "08:00:00");
    const shiftEndCst = scheduleEntry?.shiftEndCst || (member?.endHour ? withSeconds(member.endHour) : "17:00:00");
    return { shiftStartCst, shiftEndCst };
  };

  const saveEdit = async () => {
    if (!editMemberId || !editForm) return;
    const member = teamData.find((m) => m.id === editMemberId);
    if (!member) return;

    if (member.role === "lead" && editForm.role === "member") {
      const otherLeads = teamData.filter((m) => m.id !== editMemberId && m.role === "lead");
      if (otherLeads.length === 0) {
        setShowLeadWarning(true);
        return;
      }
    }

    const isRoleChange   = editForm.role !== member.role;
    const performerEmail = authUser?.email || authUser?.username || "";
    const performerGpid  = userGpid;

    setIsSaving(true);
    setSaveError(null);
    setTeamFieldError(null);

    try {
      await dispatch(updateSustainTeamMember(member.id, {
        performerEmail,
        performerGpid,
        role:                    editForm.role === "lead" ? "Lead" : "Member",
        managerEmail:            editForm.managerEmail?.trim() ?? "",
        teamId:                  editForm.teamId ?? undefined,
        channelInAppOnDuty:      editForm.channelsOnDuty.nav,
        channelEmailOnDuty:      editForm.channelsOnDuty.email,
        channelTeamsOnDuty:      editForm.channelsOnDuty.teams,
        channelInAppOffDuty:           editForm.channelsOffDuty.nav,
        channelEmailOffDuty:           editForm.channelsOffDuty.email,
        channelTeamsOffDuty:           editForm.channelsOffDuty.teams,
        triggerNewTicketOnDuty:        editForm.triggersOnDuty.newTicket,
        triggerAssignedToMeOnDuty:     editForm.triggersOnDuty.assigned,
        triggerUnassignedFromMeOnDuty: editForm.triggersOnDuty.unassigned,
        triggerCommentAddedOnDuty:     editForm.triggersOnDuty.comment,
        triggerAgingTicketOnDuty:      editForm.triggersOnDuty.agingTicket,
        triggerNewTicketOffDuty:        editForm.triggersOffDuty.newTicket,
        triggerAssignedToMeOffDuty:     editForm.triggersOffDuty.assigned,
        triggerUnassignedFromMeOffDuty: editForm.triggersOffDuty.unassigned,
        triggerCommentAddedOffDuty:     editForm.triggersOffDuty.comment,
        triggerAgingTicketOffDuty:      editForm.triggersOffDuty.agingTicket,
        notificationScope:             mapScope(editForm.scope),
        customUrgencies:               editForm.scope === "custom" ? (editForm.customUrgencies ?? []) : [],
      }, { search, filters: rosterFilters }));

      const savedName = member.name;
      setEditMemberId(null);
      setEditForm(null);
      setTimeout(() => {
        toast.show(`Settings updated for ${savedName}.`, "success");
      }, 260);
    } catch (err: any) {
      const serverMsg: string | undefined = err?.response?.data?.message;
      if (serverMsg && /team/i.test(serverMsg)) {
        setTeamFieldError(serverMsg);
      } else {
        const errMsg = serverMsg || (isRoleChange
          ? "Unable to update role. Please try again."
          : "Unable to save changes. Please try again.");
        setSaveError(errMsg);
        setTimeout(() => toast.show(errMsg, "error"), 0);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const onUpdateMemberTime = async (memberId: string, which: "start" | "end", value: string) => {
    const member = teamData.find((m) => m.id === memberId);
    if (!member) return;
    if (!isCurrentUserLead && member.email.toLowerCase() !== currentUserEmail) return;
    if (!isValidTimeValue(value)) return;

    const newStartHour = which === "start" ? value : member.startHour;
    const newEndHour   = which === "end"   ? value : member.endHour;

    setTeamData((prev) =>
      prev.map((m) =>
        m.id === memberId ? { ...m, startHour: newStartHour, endHour: newEndHour } : m
      )
    );

    const emailKey      = member.email.toLowerCase();
    const scheduleEntry = rawScheduleData.find(
      (e) => (e.memberEmail ?? "").toLowerCase() === emailKey,
    );
    if (!scheduleEntry) return;

    const scheduleJson = buildScheduleJson(emailKey, calendarDays, calendarScheduleMap, calendarToggles);
    const body: UpdateScheduleBody = {
      performerEmail: authUser?.email || authUser?.username || "",
      performerGpid:  userGpid,
      shiftStartCst:  withSeconds(newStartHour),
      shiftEndCst:    withSeconds(newEndHour),
      scheduleJson,
    };

    markRowSaving(memberId);
    try {
      await dispatch(updateSustainTeamSchedule(scheduleEntry.memberId, body) as any);
      await refreshScheduleData();
    } catch (err: any) {
      toast.show(err?.message ?? "Failed to update shift time", "error");
    } finally {
      unmarkRowSaving(memberId);
    }
  };

  const onToggleScheduleDay = async (memberId: string, day: CalendarDay) => {
    const member = teamData.find((m) => m.id === memberId);
    if (!member) return;
    const isOwnRow = member.email.toLowerCase() === currentUserEmail;
    if (!isCurrentUserLead && !isOwnRow) {
      toast.show("You can only edit your own schedule.", "error");
      return;
    }
    const emailKey = member.email.toLowerCase();
    const dateKey  = toDateKey(day.dateObj);

    const apiChecked = rawScheduleData.length > 0
      ? ((calendarScheduleMap[emailKey]?.[dateKey]) ?? false)
      : false;
    const current =
      calendarToggles[emailKey] !== undefined && dateKey in calendarToggles[emailKey]
        ? calendarToggles[emailKey][dateKey]
        : apiChecked;
    const newValue = !current;

    const newMemberToggles = { ...(calendarToggles[emailKey] ?? {}), [dateKey]: newValue };
    const newAllToggles    = { ...calendarToggles, [emailKey]: newMemberToggles };
    setCalendarToggles(newAllToggles);

    const scheduleEntry = rawScheduleData.find(
      (e) => (e.memberEmail ?? "").toLowerCase() === emailKey,
    );
    if (!scheduleEntry) return;

    const scheduleJson = buildScheduleJson(emailKey, calendarDays, calendarScheduleMap, newAllToggles);
    const { shiftStartCst, shiftEndCst } = resolveScheduleShiftTimes(scheduleEntry, member);
    const body: UpdateScheduleBody = {
      performerEmail: authUser?.email || authUser?.username || "",
      performerGpid:  userGpid,
      shiftStartCst,
      shiftEndCst,
      scheduleJson,
    };

    markRowSaving(memberId);
    try {
      await dispatch(updateSustainTeamSchedule(scheduleEntry.memberId, body) as any);
      await refreshScheduleData();
      setCalendarToggles((prev) => {
        const updated = { ...prev };
        delete updated[emailKey];
        return updated;
      });
    } catch (err: any) {
      setCalendarToggles((prev) => ({
        ...prev,
        [emailKey]: { ...(prev[emailKey] ?? {}), [dateKey]: current },
      }));
      toast.show(err?.message ?? "Failed to update schedule", "error");
    } finally {
      unmarkRowSaving(memberId);
    }
  };

  // ─── All Members Off Duty toggle state ───────────────────────────────────────
  const [allOffDutyActive, setAllOffDutyActive] = useState(false);
  const [showAllOffDutyModal, setShowAllOffDutyModal] = useState(false);
  const [allOffDutyRange, setAllOffDutyRange] = useState<AllOffDutyRange | null>(null);
  const [allOffDutyLoading, setAllOffDutyLoading] = useState(false);

  // Populate toggle state from API on screen mount
  useEffect(() => {
    let cancelled = false;
    const loadStatus = async () => {
      try {
        const status = await dispatch(fetchAllMembersOffDutyStatus() as any) as SustainAllMembersOffDutyResponse;
        if (cancelled) return;
        if (status?.enabled && status.startDate && status.startTime && status.endDate && status.endTime) {
          setAllOffDutyActive(true);
          setAllOffDutyRange({
            startDate: status.startDate,
            endDate:   status.endDate,
            startTime: status.startTime,
            endTime:   status.endTime,
          });
        } else {
          setAllOffDutyActive(false);
          setAllOffDutyRange(null);
        }
      } catch {
        // Non-fatal — toggle stays at default (off) if status fetch fails
      }
    };
    loadStatus();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Format a "YYYY-MM-DD" + "HH:MM" pair into a readable display string. */
  const formatOffDutyDateTime = (date: string, time: string): string => {
    if (!date || !time) return "";
    const [y, m, d] = date.split("-");
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const monthLabel = months[parseInt(m, 10) - 1] ?? m;
    const dayNum = parseInt(d, 10);
    const [hStr, minStr] = time.split(":");
    const h = parseInt(hStr, 10);
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    return `${monthLabel} ${dayNum}, ${y} · ${String(h12).padStart(2, "0")}:${minStr} ${ampm}`;
  };

  const handleAllOffDutyTogglePress = () => {
    if (allOffDutyActive) {
      // Turning OFF — call deactivate API
      const performerEmail = authUser?.email || authUser?.username || "";
      setAllOffDutyLoading(true);
      dispatch(deactivateAllMembersOffDuty({ performerEmail, performerGpid: userGpid || undefined }) as any)
        .then(() => {
          setAllOffDutyActive(false);
          setAllOffDutyRange(null);
          toast.show("All Members Off Duty has been deactivated.", "info");
          // Refresh team members to reflect updated off-duty status
          refetchTeamMembers();
        })
        .catch((err: any) => {
          toast.show(err?.message ?? "Failed to deactivate. Please try again.", "error");
        })
        .finally(() => setAllOffDutyLoading(false));
    } else {
      // Turning ON — open the date/time modal
      setShowAllOffDutyModal(true);
    }
  };

  const handleAllOffDutyModalCancel = () => {
    setShowAllOffDutyModal(false);
    // Toggle stays OFF (never flipped to true)
  };

  const handleAllOffDutyModalSubmit = async (range: AllOffDutyRange) => {
    const performerEmail = authUser?.email || authUser?.username || "";
    try {
      await dispatch(activateAllMembersOffDuty({
        performerEmail,
        performerGpid: userGpid || undefined,
        startDate: range.startDate,
        endDate:   range.endDate,
        startTime: range.startTime,
        endTime:   range.endTime,
      }) as any);
      setShowAllOffDutyModal(false);
      setAllOffDutyActive(true);
      setAllOffDutyRange(range);
      toast.show("All Members Off Duty activated.", "success");
      // Refresh team members to reflect updated off-duty status
      refetchTeamMembers();
    } catch (err: any) {
      setShowAllOffDutyModal(false);
      toast.show(err?.message ?? "Failed to activate. Please try again.", "error");
    }
  };

  const onToggleMemberSort = () => {
    setMemberSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const windowWidth = Dimensions.get("window").width;
  const isiPhone = Platform.OS === "ios" && windowWidth < 768;

  if (accessLoading || !isSustainTeamMember) {
    return <LHLoader />;
  }

  /* ── iPhone Mobile Layout ─────────────────────────────────────────────────── */
  if (isiPhone) {
    return <MobileTeamView teamData={teamData} onNavigate={onNavigate} />;
  }

  const editMember = teamData.find((m) => m.id === editMemberId);
  const filtered   = teamData;
  const sortedFilteredMembers = [...filtered].sort((a, b) => {
    const order = a.name.localeCompare(b.name);
    return memberSortDirection === "asc" ? order : -order;
  });
  const onDutyCount = teamData.filter((m) => m.workingStatus === "On Duty").length;
  const leadCount   = teamData.filter((m) => m.role === "lead").length;
  const memberCount = teamData.filter((m) => m.role === "member").length;

  return (
    <View style={[s.root, isDark && { backgroundColor: "#141414" }, activeView === "calendar" && s.rootCalendar]}>
      {/* ── Header ── */}
      <View style={[s.header, isDark && { backgroundColor: "#202020", borderBottomColor: "#363636" }]}>
        <View style={s.headerLeft}>
          <View>
            <View style={s.breadcrumb}>
              <Pressable onPress={() => onNavigate("Ticketing Portal")}>
                <Text style={[s.breadcrumbLink, isDark && { color: "#B0B0B0" }]}>Ticketing Portal</Text>
              </Pressable>
              <Text style={[s.breadcrumbSep, isDark && { color: "#6E6E6E" }]}> / </Text>
              <Text style={[s.breadcrumbCurrent, isDark && { color: "#F2F2F2" }]}>Team Management</Text>
            </View>
            <Text style={[s.pageTitle, isDark && { color: "#F2F2F2" }]}>Sustain Team Management</Text>
            <Text style={[s.pageSubtitle, isDark && { color: "#B0B0B0" }]}>
              {teamData.length} total, {leadCount} leads, {memberCount} members, {onDutyCount} on duty right now ({localNowTime})
            </Text>
          </View>
        </View>

        {/* Team View / Calendar View switcher */}
        <View style={[s.viewSwitcher, isDark && { borderColor: "#404040" }]}>
          <Pressable
            style={[s.switcherBtn, isDark && { backgroundColor: "#202020" }, activeView === "team" && (isDark ? { backgroundColor: "#0d2a44" } : s.switcherBtnActive)]}
            onPress={() => setActiveView("team")}
          >
            <MaterialIcons name="people" size={14} color={activeView === "team" ? (isDark ? "#3D8BFF" : "#034895") : (isDark ? "#B0B0B0" : "#6b7280")} />
            <Text style={[s.switcherBtnText, isDark && { color: "#B0B0B0" }, activeView === "team" && (isDark ? { color: "#3D8BFF" } : s.switcherBtnTextActive)]}>Team View</Text>
          </Pressable>
          <Pressable
            style={[s.switcherBtn, isDark && { backgroundColor: "#202020" }, activeView === "calendar" && (isDark ? { backgroundColor: "#0d2a44" } : s.switcherBtnActive)]}
            onPress={() => setActiveView("calendar")}
          >
            <MaterialIcons name="calendar-today" size={14} color={activeView === "calendar" ? (isDark ? "#3D8BFF" : "#034895") : (isDark ? "#B0B0B0" : "#6b7280")} />
            <Text style={[s.switcherBtnText, isDark && { color: "#B0B0B0" }, activeView === "calendar" && (isDark ? { color: "#3D8BFF" } : s.switcherBtnTextActive)]}>Calendar View</Text>
          </Pressable>
        </View>
      </View>

      {/* ── All Members Off Duty Banner (Lead-only) ── */}
      {isCurrentUserLead && (
        <Pressable
          style={[
            s.allOffDutyBanner,
            isDark && { backgroundColor: "#2B1A00", borderBottomColor: "#5A3A00" },
            allOffDutyActive && (isDark ? { backgroundColor: "#3D2608", borderBottomColor: "#F5A623" } : s.allOffDutyBannerActive),
          ]}
          onPress={handleAllOffDutyTogglePress}
          accessibilityRole="switch"
          accessibilityState={{ checked: allOffDutyActive }}
        >
          <View
            style={[
              s.allOffDutyIconWrap,
              isDark && { backgroundColor: "#4A3000" },
              allOffDutyActive && (isDark ? { backgroundColor: "#5A3A00" } : s.allOffDutyIconWrapActive),
            ]}
          >
            <MaterialIcons
              name={allOffDutyActive ? "people-outline" : "people"}
              size={18}
              color={allOffDutyActive ? (isDark ? "#FFAD5C" : "#B45309") : (isDark ? "#FFD966" : "#6b7280")}
            />
          </View>
          <View style={s.allOffDutyTextWrap}>
            <Text style={[s.allOffDutyTitle, isDark && { color: "#F2F2F2" }]}>All Team Members Off Duty</Text>
            {allOffDutyActive && allOffDutyRange ? (
              <Text style={s.allOffDutyActiveRange}>
                Active: {formatOffDutyDateTime(allOffDutyRange.startDate, allOffDutyRange.startTime)}
                {" → "}
                {formatOffDutyDateTime(allOffDutyRange.endDate, allOffDutyRange.endTime)}
              </Text>
            ) : (
              <Text style={[s.allOffDutySubtext, isDark && { color: "#B0B0B0" }]}>
                Toggling this on results in all "System Issue" category tickets to automatically create an INC in SNOW
              </Text>
            )}
          </View>
          {/* Custom toggle switch */}
          <View
            style={[
              s.allOffDutyToggleTrack,
              isDark && { backgroundColor: "#5A5A5A" },
              allOffDutyActive && s.allOffDutyToggleTrackOn,
            ]}
          >
            <View
              style={[
                s.allOffDutyToggleThumb,
                isDark && { backgroundColor: "#FFFFFF" },
                allOffDutyActive && s.allOffDutyToggleThumbOn,
              ]}
            />
          </View>
        </Pressable>
      )}

      {/* ── All Members Off Duty Modal ── */}
      <AllOffDutyModal
        visible={showAllOffDutyModal}
        onCancel={handleAllOffDutyModalCancel}
        onSubmit={handleAllOffDutyModalSubmit}
      />

      {/* ── Team View ── */}
      {activeView === "team" && (
        <ScrollView style={[s.scrollArea, isDark && { backgroundColor: "#141414" }]} contentContainerStyle={s.scrollContent}>
          <TeamTable
            sortedFilteredMembers={sortedFilteredMembers}
            teamLoading={teamLoading}
            teamLoadError={teamLoadError}
            filteredCount={filtered.length}
            search={search}
            setSearch={setSearch}
            searchFocused={searchFocused}
            setSearchFocused={setSearchFocused}
            memberSortDirection={memberSortDirection}
            onToggleMemberSort={onToggleMemberSort}
            canEditMember={canEditMember}
            onOpenEdit={openEdit}
            rosterFilters={rosterFilters}
            onRosterFiltersChange={setRosterFilters}
            managerOptions={managerOptions}
            teamOptions={teamOptions}
            allTeamOptions={allTeamOptions}
            isDark={isDark}
          />
        </ScrollView>
      )}

      {/* ── Calendar View ── */}
      {activeView === "calendar" && (
        <View style={[s.calendarScreenWrap, isDark && { backgroundColor: "#141414" }]}>
          {scheduleLoading ? (
            <CalendarViewSkeleton />
          ) : scheduleError ? (
            <View style={{ flex: 1, justifyContent: "center", alignItems: "center", paddingTop: 60 }}>
              <Text style={{ color: "#c0392b", fontSize: 14 }}>{scheduleError}</Text>
            </View>
          ) : (
            <CalendarTable
              teamData={calendarTeamData}
              calendarDays={calendarDays}
              calendarScheduleMap={calendarScheduleMap}
              calendarToggles={calendarToggles}
              rawScheduleData={rawScheduleData}
              localTimeZoneAbbr={localTimeZoneAbbr}
              isCurrentUserLead={isCurrentUserLead}
              currentUserEmail={currentUserEmail}
              currentUserGpid={userGpid}
              savingMemberIds={savingMemberIds}
              savingAnimValue={savingAnimValue}
              onToggleScheduleDay={onToggleScheduleDay}
              onUpdateMemberTime={onUpdateMemberTime}
              onRefreshScheduleData={refreshScheduleData}
              teamFilterOptions={teamOptions}
              managerFilterOptions={calendarManagerOptions}
              selectedTeams={calendarTeamFilters}
              selectedManagers={calendarManagerFilters}
              onTeamsChange={setCalendarTeamFilters}
              onManagersChange={setCalendarManagerFilters}
              filteredMemberCount={calendarMemberCount}
            />
          )}
        </View>
      )}

      {/* ── Edit Member Panel ── */}
      <EditMemberPanel
        editMember={editMember}
        editForm={editForm}
        setEditForm={setEditForm}
        panelVisible={panelVisible}
        slideAnim={slideAnim}
        isSaving={isSaving}
        saveError={saveError}
        showUnsavedDialog={showUnsavedDialog}
        setShowUnsavedDialog={setShowUnsavedDialog}
        showLeadWarning={showLeadWarning}
        setShowLeadWarning={setShowLeadWarning}
        showRoleDropdown={showRoleDropdown}
        setShowRoleDropdown={setShowRoleDropdown}
        isCurrentUserLead={isCurrentUserLead}
        leadOptions={leadOptions}
        teamOptions={allTeamOptions}
        teamAssignmentOptions={teamOptions}
        teamFieldError={teamFieldError}
        closeEdit={closeEdit}
        saveEdit={saveEdit}
        discardAndClose={discardAndClose}
      />
    </View>
  );
};

export default SustainTeamManagementScreen;
